import * as React from 'react';
import { IProfile, Profile } from '@se360/header-components';
import LanguageService, {
    setDateLocale,
    setMomentLocale,
    Translate
} from '../../../../../../../shared/LanguageService';
import { useUpdateUserProfile } from '../hooks/useUpdateUserProfile';
import { handleError } from '../../../../../../../shared/ErrorCatching';
import { transformProfileForModal, transformProfileForPhoto, UserProfile } from '../models/profile';
import { useUserContext } from '../../../../../../../shared/context/user-context';
import { Language, TimeZone } from '../ProfileApi';
import { ProfileLabels } from '@se360/header-components/dist/profile/Profile';
import notify from '../../../../../../../shared/feedback/notifications';

type ExternalProps = {
    open: boolean;
    onClose: () => void;
    userProfile: UserProfile;
    availableLanguages: Language[];
    availableTimeZones: TimeZone[];
};

type TranslationLayerProps = {
    changePasswordUrl: string;
    authorityUrl: string;
    userProfile: UserProfile;
    accessToken: string;
    onUpdateUserProfile: (userProfile: UserProfile) => Promise<void>;
    availableLanguages: Language[];
    availableTimeZones: TimeZone[];
    apiUrl: string;
    userCountryId: number;
} & ExternalProps;

type ProfileModalProps = {
    labels: ProfileLabels;
} & TranslationLayerProps &
    ExternalProps;

const ProfileModal = ({
    open,
    onClose,
    labels,
    changePasswordUrl,
    userProfile,
    authorityUrl,
    accessToken,
    onUpdateUserProfile,
    availableLanguages,
    availableTimeZones,
    apiUrl,
    userCountryId
}: ProfileModalProps) => {
    const handleSubmit = (profile: IProfile, error?) => {
        if (error) {
            if (error.response) {
                handleError(error);
            } else {
                notify.failure(labels.errorMessage);
            }
        } else {
            let updatedProfile: UserProfile = transformProfileForPhoto(profile);

            onUpdateUserProfile(updatedProfile)
                .then(() => {
                    notify.success(labels.successMessage);
                    onClose();
                })
                .catch((error) => {
                    handleError(error);
                });
        }
    };

    return (
        <Profile
            key={userProfile.email}
            open={open}
            onClose={onClose}
            onSave={handleSubmit}
            user={transformProfileForModal(userProfile)}
            labels={labels}
            onChangePasswordUrl={changePasswordUrl}
            url={authorityUrl}
            token={accessToken}
            availableLanguages={availableLanguages}
            availableTimeZones={availableTimeZones}
            apiUrl={apiUrl}
            countryId={userCountryId}
        />
    );
};

const ProfileComponentTranslationLayer = (props: TranslationLayerProps) => {
    // TODO: Add missing labels..
    let labels: ProfileLabels = {
        title: Translate('user-profile/title'),
        name: Translate('user-profile/name'),
        password: Translate('user-profile/password'),
        contactDetails: Translate('user-profile/contact-details'),
        languageAndTimeZone: Translate('user-profile/language-timezone'),
        textFieldFirstName: Translate('user-profile/textfield-first-name'),
        textFieldSurName: Translate('user-profile/textfield-last-name'),
        textFieldCellPhone: Translate('user-profile/textfield/primary-phone-number'),
        textFieldPhoneNumber: Translate('user-profile/textfield/secondary-phone-number'),
        textFieldUserName: Translate('user-profile/textfield-user-name'),
        textFieldEmail: Translate('user-profile/textfield-email'),
        selectLanguage: Translate('user-profile/select-language'),
        selectTimeZone: Translate('user-profile/select-timezone'),
        buttonSaveExit: Translate('user-profile/save-and-exit-button'),
        footerTitle: Translate('user-profile/footer-title'),
        supportEmail: Translate('user-profile/support-email'),
        helperTextChangeUsername: Translate('user-profile/helper-text-change-username'),
        resetPassword: Translate('user-profile/reset-password'),
        selectLanguage_sv: Translate('language/select-option/swedish'),
        selectLanguage_en: Translate('language/select-option/english'),
        selectLanguage_fi: Translate('language/select-option/finnish'),
        helperTextRequiredEmail: Translate('user-profile/helper-text-email'),
        helperTextRequiredFirstName: Translate('user-profile/helper-text-firstname'),
        helperTextRequiredLastName: Translate('user-profile/helper-text-lastname'),
        uploadImage: Translate('user-profile/upload-image'),
        deleteImage: Translate('user-profile/tooltip-delete-image'),
        helperTextInvalidPhone: Translate('user-profile/invalid-phone'),
        errorMessage: Translate('user-profile/error-update'),
        successMessage: Translate('user-profile/success-update')
    };

    return <ProfileModal {...props} labels={labels} />;
};

const ProfileComponentDataLayer = ({
    open,
    onClose,
    userProfile,
    availableLanguages,
    availableTimeZones
}: ExternalProps & {
    loadLanguage: (language: string, applicationVersion: string) => void;
}) => {
    let { mutateAsync: updateProfile } = useUpdateUserProfile<UserProfile>();
    let { user } = useUserContext();

    const handleUpdateUserProfile = async (profile: UserProfile) => {
        await updateProfile(profile);

        if (userProfile.languageKey !== profile.languageKey) {
            setMomentLocale(profile.languageKey);
            setDateLocale(profile.languageKey);

            let language = await LanguageService.getLanguageFromApi(profile.languageKey);
            LanguageService.setLanguage(language);
            LanguageService.setLanguageInLocaleStorage(language);

            // TODO: Might be possible to not have to force a total reload of the entire page?
            window.location.reload();
        }
    };

    let { authority } = window.appConfig.acfg;

    let { apiUrl } = window.appConfig;

    let userCountryId = user.details.country;

    return (
        <ProfileComponentTranslationLayer
            changePasswordUrl={authority}
            userProfile={userProfile}
            onUpdateUserProfile={handleUpdateUserProfile}
            authorityUrl={authority}
            accessToken={user.access_token}
            availableLanguages={availableLanguages}
            availableTimeZones={availableTimeZones}
            onClose={onClose}
            open={open}
            apiUrl={apiUrl}
            userCountryId={userCountryId}
        />
    );
};

export default ProfileComponentDataLayer;
